/**
 * Data audit view for the CashFlow page.
 * Renders raw tables for every cashflow data source so you can verify what
 * landed in D1 after a sync, CSV upload, or quick-add. Info-dense, scrollable,
 * no aesthetic concessions.
 */

import { useEffect, useState } from 'react';
import { fetchSyncJobs, type SyncJob } from '../../utils/api';
import type {
  MonthlySummary,
  Asset,
  Liability,
  QuickAddEntry,
} from '../../types/cashflow';
import { formatCurrency } from '../../utils/cashflowCalc';

interface Props {
  summaries: MonthlySummary[];
  assets: Asset[];
  liabilities: Liability[];
  quickAdds: QuickAddEntry[];
}

// Extra columns on the server-side rows that the typed FE doesn't expose
type AssetWithMeta = Asset & { id?: string; source?: string; source_id?: string | null };
type LiabilityWithMeta = Liability & { id?: string; source?: string; source_id?: string | null };

export default function DataAuditPanel({
  summaries,
  assets,
  liabilities,
  quickAdds,
}: Props) {
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSyncJobs(30)
      .then((data) => {
        if (!cancelled) setJobs(data);
      })
      .catch((err) => {
        if (!cancelled) setJobsError(err instanceof Error ? err.message : 'Failed to load jobs');
      })
      .finally(() => {
        if (!cancelled) setJobsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sort summaries newest first for the audit view (easier to spot the latest sync)
  const sortedSummaries = [...summaries].sort((a, b) => b.month.localeCompare(a.month));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
      {/* Sync jobs first - most useful for debugging recent activity */}
      <Section
        title="Recent sync jobs"
        subtitle={`${jobs.length} most recent`}
        empty={jobsLoading ? 'Loading…' : jobsError ?? (jobs.length === 0 ? 'No sync jobs yet.' : null)}
      >
        {jobs.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Started</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Written</th>
                <th style={thStyle}>Source sheet</th>
                <th style={thStyle}>Error</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const written =
                  (j.response_payload as { written?: number } | null)?.written ?? null;
                const sourceId =
                  (j.request_payload as { source_id?: string } | null)?.source_id ?? null;
                const sheetLabel =
                  (j.request_payload as { sheet_label?: string } | null)?.sheet_label ?? null;
                return (
                  <tr key={j.id} style={trStyle}>
                    <td style={tdStyle}>{shortTime(j.started_at)}</td>
                    <td style={tdStyle}><code>{j.route}</code></td>
                    <td style={tdStyle}>{statusBadge(j.status)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {written !== null ? written : '—'}
                    </td>
                    <td style={tdStyle}>
                      {sheetLabel ?? (sourceId ? <code>{sourceId.slice(0, 8)}…</code> : '—')}
                    </td>
                    <td style={tdStyle}>
                      {j.error_message ? (
                        <span style={{ color: '#b91c1c' }}>{j.error_message}</span>
                      ) : j.validation_errors && j.validation_errors.length > 0 ? (
                        <span style={{ color: '#b45309' }}>
                          {j.validation_errors.length} validation warning(s)
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Monthly summaries"
        subtitle={`${summaries.length} rows`}
        empty={summaries.length === 0 ? 'No summaries yet.' : null}
      >
        {summaries.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Month</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Earned</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Passive</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Portfolio</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Tax</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Housing</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Living</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Debt</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Other</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cashflow</th>
              </tr>
            </thead>
            <tbody>
              {sortedSummaries.map((s) => {
                const totalIn = s.earned_income + s.passive_income + s.portfolio_income;
                const totalOut =
                  s.tax_expenses + s.housing_expenses + s.living_expenses +
                  s.debt_expenses + s.other_expenses;
                const flow = totalIn - totalOut;
                return (
                  <tr key={s.month} style={trStyle}>
                    <td style={tdStyle}><code>{s.month}</code></td>
                    <td style={numTd}>{fmtNum(s.earned_income)}</td>
                    <td style={numTd}>{fmtNum(s.passive_income)}</td>
                    <td style={numTd}>{fmtNum(s.portfolio_income)}</td>
                    <td style={numTd}>{fmtNum(s.tax_expenses)}</td>
                    <td style={numTd}>{fmtNum(s.housing_expenses)}</td>
                    <td style={numTd}>{fmtNum(s.living_expenses)}</td>
                    <td style={numTd}>{fmtNum(s.debt_expenses)}</td>
                    <td style={numTd}>{fmtNum(s.other_expenses)}</td>
                    <td style={{ ...numTd, fontWeight: 700, color: flow >= 0 ? '#059669' : '#b91c1c' }}>
                      {flow >= 0 ? '+' : ''}{fmtNum(flow)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Assets"
        subtitle={`${assets.length} rows`}
        empty={assets.length === 0 ? 'No assets yet. Add via Quick-Add or sync.' : null}
      >
        {assets.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Month</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Current value</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Monthly cashflow</th>
                <th style={thStyle}>Source</th>
              </tr>
            </thead>
            <tbody>
              {(assets as AssetWithMeta[]).map((a) => (
                <tr key={a.id ?? `${a.month}-${a.name}`} style={trStyle}>
                  <td style={tdStyle}><code>{a.month}</code></td>
                  <td style={tdStyle}>{a.name}</td>
                  <td style={tdStyle}><code>{a.category}</code></td>
                  <td style={numTd}>{formatCurrency(a.current_value)}</td>
                  <td style={numTd}>{formatCurrency(a.monthly_cashflow)}</td>
                  <td style={tdStyle}>{sourceBadge(a.source, a.source_id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Liabilities"
        subtitle={`${liabilities.length} rows`}
        empty={liabilities.length === 0 ? 'No liabilities yet. Add via Quick-Add or sync.' : null}
      >
        {liabilities.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Month</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Monthly</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>APR</th>
                <th style={thStyle}>Source</th>
              </tr>
            </thead>
            <tbody>
              {(liabilities as LiabilityWithMeta[]).map((l) => (
                <tr key={l.id ?? `${l.month}-${l.name}`} style={trStyle}>
                  <td style={tdStyle}><code>{l.month}</code></td>
                  <td style={tdStyle}>{l.name}</td>
                  <td style={tdStyle}><code>{l.category}</code></td>
                  <td style={numTd}>{formatCurrency(l.outstanding_balance)}</td>
                  <td style={numTd}>{formatCurrency(l.monthly_payment)}</td>
                  <td style={numTd}>{l.interest_rate}%</td>
                  <td style={tdStyle}>{sourceBadge(l.source, l.source_id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Quick-add entries"
        subtitle={`${quickAdds.length} rows`}
        empty={quickAdds.length === 0 ? 'No quick-add entries yet.' : null}
      >
        {quickAdds.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Note</th>
              </tr>
            </thead>
            <tbody>
              {quickAdds.map((q) => (
                <tr key={q.id} style={trStyle}>
                  <td style={tdStyle}><code>{q.date}</code></td>
                  <td style={tdStyle}>{typeBadge(q.type)}</td>
                  <td style={tdStyle}>{q.name}</td>
                  <td style={tdStyle}>{q.category ? <code>{q.category}</code> : '—'}</td>
                  <td style={numTd}>{formatCurrency(q.amount)}</td>
                  <td style={tdStyle}>{q.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

// ---------- Small helpers ----------

function Section({
  title,
  subtitle,
  empty,
  children,
}: {
  title: string;
  subtitle?: string;
  empty: string | null;
  children?: React.ReactNode;
}) {
  return (
    <section className="card" style={{ padding: 'var(--spacing-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
        {subtitle && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{subtitle}</span>
        )}
      </div>
      {empty ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', margin: 0 }}>{empty}</p>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>{children}</div>
      )}
    </section>
  );
}

function fmtNum(n: number): string {
  if (n === 0) return '·';
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortTime(iso: string): string {
  try {
    return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString();
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  const map: Record<string, { bg: string; fg: string }> = {
    success: { bg: '#dcfce7', fg: '#166534' },
    error: { bg: '#fee2e2', fg: '#991b1b' },
    running: { bg: '#dbeafe', fg: '#1e40af' },
    pending: { bg: '#fef3c7', fg: '#92400e' },
    timeout: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const cfg = map[status] ?? { bg: '#f4f4f4', fg: '#525252' };
  return <span style={{ ...badgeStyle, background: cfg.bg, color: cfg.fg }}>{status}</span>;
}

function sourceBadge(source: string | undefined, sourceId: string | null | undefined) {
  const label = source ?? 'unknown';
  const map: Record<string, { bg: string; fg: string }> = {
    sync: { bg: '#dbeafe', fg: '#1e40af' },
    csv: { bg: '#fef3c7', fg: '#92400e' },
    quick_add: { bg: '#e9d5ff', fg: '#6b21a8' },
    demo: { bg: '#f4f4f4', fg: '#525252' },
  };
  const cfg = map[label] ?? { bg: '#f4f4f4', fg: '#525252' };
  return (
    <span title={sourceId ?? ''} style={{ ...badgeStyle, background: cfg.bg, color: cfg.fg }}>
      {label}
    </span>
  );
}

function typeBadge(type: string) {
  return <span style={{ ...badgeStyle, background: '#f4f4f4', color: '#525252' }}>{type}</span>;
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8rem',
  fontVariantNumeric: 'tabular-nums',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  fontWeight: 700,
  position: 'sticky',
  top: 0,
  background: '#fff',
  borderBottom: '1px solid var(--color-border)',
};

const tdStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid #f1f1f1',
};

const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const trStyle: React.CSSProperties = {
  verticalAlign: 'top',
};

const badgeStyle: React.CSSProperties = {
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: '0.7rem',
  fontWeight: 600,
  display: 'inline-block',
};
